import querystring from 'query-string';

import * as clientErrTypes from 'config/clientErrorTypes';
import {
    MOCK_API_ENDPOINT,
    FEVER_API_ENDPOINT,
    CLIENT_API_FEVER_HOST,
    ZINES_API_ENDPOINT
} from 'config/envVariable';
import { GET, POST, PUT, DELETE, normalizeResponse } from 'api/common';
import tokenManager from 'client/utils/tokenManager';
import { loginTypesForApi } from 'config/loginTypes';
import { NOTIFY_DELIVER_TYPE_MAP } from 'config/notifyTypes';
import { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import {
    GET_PRIZE_WINNER_STATUS_API_RESPONSE,
    GET_USER_POINTS_API_RESPONSE,
    STORE_GAME_RESULT_API_RESPONSE,
    STORE_ENTRY_SHARE_RECORD_API_RESPONSE,
    POST_NOTIFY_API_RESPONSE,
    RETRY_AI_JOB_API_RESPONSE
} from 'apiHandler/client';
import { GET_FORM_ANSWER_API_RESPONSE } from 'apiHandler/form';
import { receiptCarrierCodes } from 'apiHandler/receiptCarrier';
import { REDEEM_POINT_API_RESPONSE } from 'apiHandler/point';
import { isJSONParsable } from 'utils/objectManipulate';
import { sentryCaptureException } from 'sentry';

// 監控 hook（可選）
let apiMonitor = null;

/**
 * 設定 API 監控
 * @param {Function} monitor - 監控函數
 */
export function setApiMonitor(monitor) {
    apiMonitor = monitor;
}

/**
 * 清除 API 監控（只清除自己當初 set 的那個 monitor，避免覆蓋後續 instance）
 * @param {Function} [monitor] - 建立時取得的 monitor ref；省略則無條件清除（向後相容）
 */
export function clearApiMonitor(monitor) {
    if (monitor && apiMonitor !== monitor) return;
    apiMonitor = null;
}

export function getTestQueryString() {
    const { test_code, test_expired } = querystring.parse(window.location.search);
    return test_code && test_expired
        ? { 'preview-code': test_code, 'expired-at': test_expired }
        : null;
}

export function throwError(resp, throwErrConfigs = [], body = '') {
    const configs = Object.entries(throwErrConfigs);

    for (const [errType, checker] of configs) {
        if (typeof checker === 'function' && checker(resp)) {
            const apiBody = isJSONParsable(body) ? JSON.parse(body) : body;

            throw generateApiErrObj(resp, errType, apiBody);
        }
    }
}

const validateApiPath = path => {
    if (path.includes('//') || path.includes('/undefined/')) {
        sentryCaptureException(
            generateApiErrObj({}, clientErrTypes.CALLED_EMPTY_PARAMS_URL, {
                path
            })
        );
    }
};

export async function fetches(
    path,
    optionsGetter,
    { passPayload = false, dataKeys, throwErrConfigs, isMock = false } = {}
) {
    // 如果有監控，使用監控版本
    if (apiMonitor) {
        // 建立原始 fetches 函數
        const originalFetches = async (path, optionsGetter, config) => {
            const options =
                typeof optionsGetter === 'function' ? await optionsGetter() : optionsGetter;
            const baseUrl = config.isMock ? MOCK_API_ENDPOINT : FEVER_API_ENDPOINT;

            validateApiPath(path);

            // monitor 透過 config.onRequestSent / config.onHeaders / config.onBodyDone
            // 觀察三個時間切點：
            // - onRequestSent: fetch() 真正被呼叫（此前都還在 await optionsGetter()，
            //   也就是 tokenManager.getAccessToken() 可能自己去打 /auth/client/token 的那段）
            // - onHeaders: fetch() Promise resolve（response headers 抵達 ≈ TTFB 結束）
            // - onBodyDone: response body 解析完成
            // 這是替 Resource Timing API 在缺少 Timing-Allow-Origin 時取得階段資訊的唯一可靠來源
            //
            // onHeaders 會把原始 response 一併帶出去：逾時事件本身只能證明「還沒回來」，
            // 要接得上後端 log 就得靠 httpStatus 與 trace header，而這裡是唯一還拿得到
            // 原始 Response 物件的地方（normalizeResponse 之後就只剩 headers）
            const safeInvoke = (fn, ...args) => {
                if (typeof fn !== 'function') return;
                try {
                    fn(...args);
                } catch {}
            };

            safeInvoke(config.onRequestSent);
            // 弱掃誤判：URL 的 scheme + host 來自 config/envVariable 的 build-time 常數
            // FEVER_API_ENDPOINT（https://api.feversocial.com / https://api.dev.feversocial.com），
            // 全專案無任何 re-assign，不會從 query string、window 或使用者輸入取得。
            // 插值只出現在 host 之後的 path segment，字串串接無法改寫已固定的 scheme/host。
            // 且本專案為瀏覽器端 React SPA，請求由使用者自己的瀏覽器發出，不存在
            // 「伺服器代打內網」的 SSRF 攻擊面。
            // path 由 src/api/client.js 各函式以字面字串組出（如 /promotions/${promoId}/...），皆為相對路徑；
            // 上方 validateApiPath(path) 另會把 // 或 /undefined/ 這類組錯的路徑回報 Sentry。
            // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
            return fetch(`${baseUrl}${path}`, options)
                .then(response => {
                    safeInvoke(config.onHeaders, response);
                    return normalizeResponse(response);
                })
                .then(parsed => {
                    safeInvoke(config.onBodyDone);
                    return parsed;
                })
                .then(({ httpStatus, payload, headers }) => {
                    const resp = {
                        httpStatus,
                        method: options.method,
                        headers,
                        body: Boolean(options.body) ? JSON.stringify(options.body) : null,
                        payload
                    };

                    throwError(resp, config.throwErrConfigs, options.body);
                    if (resp.httpStatus === 200 && Boolean(config.dataKeys) && payload) {
                        resp.payload = payload[config.dataKeys];
                    }
                    if (resp.httpStatus === 200 && Boolean(config.passPayload)) {
                        if (Boolean(config.dataKeys) && payload) return payload[config.dataKeys];
                        else return payload;
                    }
                    return resp;
                });
        };

        // 使用監控版本
        return apiMonitor(originalFetches, path, optionsGetter, {
            passPayload,
            dataKeys,
            throwErrConfigs,
            isMock
        });
    }

    // 沒有監控時，使用原本的實作
    const options = typeof optionsGetter === 'function' ? await optionsGetter() : optionsGetter;
    const baseUrl = isMock ? MOCK_API_ENDPOINT : FEVER_API_ENDPOINT;

    validateApiPath(path);

    // 弱掃誤判：URL 的 scheme + host 來自 config/envVariable 的 build-time 常數
    // FEVER_API_ENDPOINT（https://api.feversocial.com / https://api.dev.feversocial.com），
    // 全專案無任何 re-assign，不會從 query string、window 或使用者輸入取得。
    // 插值只出現在 host 之後的 path segment，字串串接無法改寫已固定的 scheme/host。
    // 且本專案為瀏覽器端 React SPA，請求由使用者自己的瀏覽器發出，不存在
    // 「伺服器代打內網」的 SSRF 攻擊面。
    // path 由 src/api/client.js 各函式以字面字串組出（如 /promotions/${promoId}/...），皆為相對路徑；
    // 上方 validateApiPath(path) 另會把 // 或 /undefined/ 這類組錯的路徑回報 Sentry。
    // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
    return fetch(`${baseUrl}${path}`, options)
        .then(normalizeResponse)
        .then(({ httpStatus, payload, headers }) => {
            const resp = {
                httpStatus,
                method: options.method,
                headers,
                body: Boolean(options.body) ? JSON.stringify(options.body) : null,
                payload
            };

            throwError(resp, throwErrConfigs, options.body);
            if (resp.httpStatus === 200 && Boolean(dataKeys) && payload) {
                resp.payload = payload[dataKeys];
            }
            if (resp.httpStatus === 200 && Boolean(passPayload)) {
                if (Boolean(dataKeys) && payload) return payload[dataKeys];
                else return payload;
            }
            return resp;
        });
}

export function generateFetchOpts({
    method = GET,
    headers = {},
    body = null,
    formData,
    apiVersion = 1,
    token,
    feverHost,
    ...rest
} = {}) {
    return {
        method,
        headers: new Headers({
            'Fever-Host': CLIENT_API_FEVER_HOST,
            ...(feverHost ? { 'Fever-Host': feverHost } : { 'Fever-Host': CLIENT_API_FEVER_HOST }),
            Accept: `application/x.api.v${apiVersion}+json`,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(formData ? {} : { 'Content-Type': 'application/json; charset=utf-8' }),
            ...headers
        }),
        ...(method !== GET ? { body: formData ? formData : body && JSON.stringify(body) } : {}),
        ...rest
    };
}

export function generateFetchOptsGetter(options) {
    return async () => {
        const { token, errorResp, loginType } = await tokenManager.getAccessToken();
        if (errorResp) {
            throw generateApiErrObj(errorResp);
        }

        return loginType === loginTypesForApi.INSTAGRAM
            ? generateFetchOpts({ ...options, headers: { 'Fever-Entry-Token': token } })
            : generateFetchOpts({ ...options, token });
    };
}

export function getFormClientData({ formId, isPreview = false, entryUuid }) {
    const qsObj = {};
    if (isPreview) {
        qsObj.mode = 'preview';
    }
    if (entryUuid) {
        qsObj.entryUuid = entryUuid;
    }
    const qs = querystring.stringify(qsObj);
    return fetches(`/forms/${formId}${qs ? `?${qs}` : ''}`, generateFetchOpts(), {
        dataKeys: 'items',
        throwErrConfigs: {
            [clientErrTypes.NOT_FOUND]: r => r.httpStatus === 404,
            [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
        }
    });
}

export function getRedirectFormUrl({ formId, entryUuid, promoId }) {
    const qs = querystring.stringify({ promotionId: promoId, entryUuid });
    return fetches(
        `/forms/${formId}/redirect-url${qs ? `?${qs}` : ''}`,
        generateFetchOptsGetter(),
        {
            dataKeys: 'items',
            throwErrConfigs: {
                [clientErrTypes.NOT_FOUND]: r => r.httpStatus === 404,
                [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
            }
        }
    );
}

export function uploadFormAttachFile(file) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return fetches('/uploads', generateFetchOpts({ method: POST, formData }), {
        dataKeys: 'items'
    });
}

export function submitForm(formId, payload, headers, redeemToken) {
    const testQs = getTestQueryString();
    const qs = testQs ? `?${querystring.stringify(testQs)}` : '';
    const fetchOpts = Boolean(redeemToken)
        ? generateFetchOpts({
              method: POST,
              body: { ...payload, redeemToken },
              headers
          })
        : generateFetchOptsGetter({
              method: POST,
              body: payload,
              headers
          });
    return fetches(`/forms/${formId}/submit${qs}`, fetchOpts, {
        dataKeys: 'items',
        throwErrConfigs: {
            [clientErrTypes.STORE_GAME_RESULT_ENTRY_FLOW_EXISTS]: r =>
                STORE_GAME_RESULT_API_RESPONSE.ENTRY_FLOW_EXISTS(r?.payload || {}) ||
                STORE_GAME_RESULT_API_RESPONSE.ENTRY_FLOW_GAME_RESULT_EXISTS(r?.payload || {}),
            [clientErrTypes.STORE_GAME_RESULT_ENTRY_TOTAL_LIMIT]: r =>
                STORE_GAME_RESULT_API_RESPONSE.ENTRY_TOTAL_LIMIT(r?.payload || {})
        }
    });
}

export function checkTrackingStatus({ promoUUID, entriesUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/tracks`,
        generateFetchOptsGetter()
    );
}

export function getMemberTags({ promoUUID, entryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/condition-data`,
        generateFetchOptsGetter({ method: POST }),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.GET_MEMBER_TAGS_AUTH_FAILED]: r => r.httpStatus === 401,
                [clientErrTypes.GET_MEMBER_TAGS_FAILED]: r => r.httpStatus !== 200
            }
        }
    );
}

export function checkIsQualifyToGetWinner({ promoUUID, entriesUUID, lotteryUUID } = {}) {
    const testQs = getTestQueryString();
    const qs = testQs ? `?${querystring.stringify(testQs)}` : '';
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/validation/lotteries/${lotteryUUID}${qs}`,
        generateFetchOptsGetter({ method: POST }),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.GET_WINNER_QUALIFY]: r =>
                    r.httpStatus !== 200 && r.httpStatus !== 226
            }
        }
    );
}

export function getWinner(promoUUID, entriesUUID, lotteryUUID, pickPrizes) {
    const testQs = getTestQueryString();
    const qs = testQs ? `?${querystring.stringify(testQs)}` : '';
    const options = { method: POST };

    if (pickPrizes) {
        options.body = { pickPrizes };
    }

    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/winner${
            lotteryUUID ? `/${lotteryUUID}` : ''
        }${qs}`,
        generateFetchOptsGetter(options),
        { dataKeys: 'data' }
    );
}

export function updateWinnerAnimationFlag(promoUUID, entryUUID, prizeUUID) {
    const options = {
        body: {
            prizes: Array.isArray(prizeUUID) ? prizeUUID : [prizeUUID]
        },
        method: PUT
    };

    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/winner/prizes/animation`,
        generateFetchOptsGetter(options),
        {
            throwErrConfigs: {
                [clientErrTypes.PRIZE_OPEN_WINNER]: r => r.httpStatus !== 200
            }
        }
    );
}

export function updateEntryFlowLotteryParticipant({ promoUUID, entryUUID, lotteryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/lotteries/${lotteryUUID}`,
        generateFetchOptsGetter({ method: PUT }),
        {
            throwErrConfigs: {
                [clientErrTypes.PRIZE_OPEN_WINNER]: r => r.httpStatus !== 200
            }
        }
    );
}

export function getEntriesListApi(promoUUID) {
    return fetches(`/promotions/${promoUUID}/entries?ts=${Date.now()}`, generateFetchOptsGetter(), {
        dataKeys: 'data',
        throwErrConfigs: {
            [clientErrTypes.GET_HAS_GAME_RESULT_RECORDS]: r => r.httpStatus !== 200
        }
    });
}

export function getVoteResultApi(formId) {
    return fetches(`/forms/${formId}/votes`, generateFetchOptsGetter(), { dataKeys: 'items' });
}

export function getPrizeListApi(promoUUID) {
    return fetches(`/promotions/${promoUUID}/winners`, generateFetchOptsGetter(), {
        dataKeys: 'data',
        throwErrConfigs: {
            [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
        }
    });
}

export function getSponsorPrizeListApi(sponsorUUID, loginType, promoUUID, fetchOptions) {
    const qs = promoUUID ? `?promotionUuid=${promoUUID}` : '';

    return fetches(
        `/promotions/members/${sponsorUUID}/prizes/${loginType}${qs ? `${qs}` : ''}`,
        generateFetchOptsGetter(fetchOptions),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
            }
        }
    );
}

export function getPrizeInfoAPI(promoUUID, prizeUUID) {
    return fetches(`/promotions/${promoUUID}/prizes/${prizeUUID}`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

export function getPrizeWinnerStatusAPI(promoUUID, entryUUID, prizeUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/winner/prizes/${prizeUUID}`,
        generateFetchOpts(),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.INVALID_PRIZE_PAGE_WINNER_INFO]: r =>
                    r.httpStatus === 500 && Number(r.payload.status_code) === 500,
                [clientErrTypes.WINNER_NOT_FOUND]: r =>
                    GET_PRIZE_WINNER_STATUS_API_RESPONSE.WINNER_NOT_FOUND(r?.payload || {}),
                [clientErrTypes.PRIZE_PAGE_WINNER_INFO]: r => r.httpStatus !== 200
            }
        }
    );
}

export function getWinnerPrizesByEntry({ promoUUID, entryUUID, lotteryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/winner/${lotteryUUID}?ts=${Date.now()}`,
        generateFetchOptsGetter(),
        {
            dataKeys: 'data',
            throwErrConfigs: { [clientErrTypes.PRIZE_PAGE_WINNER_INFO]: r => r.httpStatus !== 200 }
        }
    );
}

export function getRemainPrizeAPI(promoUUID, lotteryUUID) {
    return fetches(
        `/promotions/${promoUUID}/lotteries/${lotteryUUID}/remainder`,
        generateFetchOpts(),
        {
            dataKeys: 'data',
            throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
        }
    );
}

export function sendMailAPI(promoUUID, entriesUUID, prizeUUID, body) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/winner/prizes/${prizeUUID}/notification`,
        generateFetchOptsGetter({ method: POST, body }),
        { dataKeys: 'data' }
    );
}

export function getRedeemPrizeUrlAPI(promoUUID, entriesUUID, prizeUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/winner/prizes/${prizeUUID}/redeem-link?domain=${encodeURIComponent(
            window.location.origin
        )}`,
        generateFetchOptsGetter(),
        { dataKeys: 'data' }
    );
}

export function storeRedeemAPI(promoUUID, entriesUUID, prizeUUID, redeemToken) {
    const fetchOptions = Boolean(redeemToken)
        ? generateFetchOpts({ method: PUT, body: { redeemToken } })
        : generateFetchOptsGetter({ method: PUT });
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/winner/prizes/${prizeUUID}/redeem`,
        fetchOptions,
        { dataKeys: 'data' }
    );
}

export function getPrizeVisualNumber(promoUUID, entriesUUID, prizeUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entriesUUID}/winner/prizes/${prizeUUID}/virtual-number`,
        generateFetchOpts()
    );
}

export function getPartnerSponsorInfo(sponsorUUID) {
    return fetches(`/promotions/sponsors/${sponsorUUID}/info`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: {
            [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
        }
    });
}

export function getPromoInfo({ promoUUID, feverHost } = {}) {
    return fetches(`/promotions/${promoUUID}`, generateFetchOpts({ feverHost }), {
        dataKeys: 'data',
        throwErrConfigs: {
            [clientErrTypes.NOT_FOUND]: r => r.httpStatus === 400 && r?.payload?.message === '404',
            [clientErrTypes.GET_API_ERROR_AND_NO_REFRESH_BTN]: r => r.httpStatus === 403,
            [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
        }
    });
}

export function getPromoInfoApi({ promoUUID, feverHost } = {}) {
    return fetches(`/promotions/${promoUUID}/infos`, generateFetchOpts({ feverHost }), {
        dataKeys: 'data',
        passPayload: true,
        throwErrConfigs: {
            [clientErrTypes.PROMO_NOT_FOUND]: r =>
                r.httpStatus === 400 && r?.payload?.code === 'PMM-R1-FNF1',
            [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200
        }
    });
}

// 開機去重快取：generateCSP 開機時會打一次 infos 算 CSP，useInitPromo 掛載後又會打同一支
// infos 載入活動資料。兩者同一頁載入、同 uuid、同 payload，這裡讓第二次重用第一次的
// in-flight / resolved promise，避免後端被同一份大 payload 問兩次（見 ClickUp #86ey1gtvm）。
let bootPromoInfoCache = null; // { promoUUID, promise }

// generateCSP 專用：照常打 infos，並把 promise 暫存供 useInitPromo 重用
export function cacheBootPromoInfo({ promoUUID, feverHost } = {}) {
    const promise = getPromoInfoApi({ promoUUID, feverHost });
    bootPromoInfoCache = { promoUUID, promise };
    return promise;
}

// useInitPromo 專用：若開機已快取同一支 promo，重用該 promise 並立即清掉（consume-once，
// 避免後續 re-init / turnstile re-trigger 拿到開機時的舊資料）；否則 fresh fetch。
export function consumeBootPromoInfo({ promoUUID, feverHost } = {}) {
    if (bootPromoInfoCache && bootPromoInfoCache.promoUUID === promoUUID) {
        const { promise } = bootPromoInfoCache;
        bootPromoInfoCache = null;
        return promise;
    }
    return getPromoInfoApi({ promoUUID, feverHost });
}

export function getUgcInfoApi({ promoId, promoUUID, feverHost } = {}) {
    return fetches(
        `/promotions/${promoUUID ?? promoId}/ugc/info`,
        generateFetchOpts({ feverHost }),
        {
            dataKeys: 'data',
            passPayload: true,
            throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
        }
    );
}

export function getFormInfo({ formId, isPreview, entryUuid }) {
    return getFormClientData({ formId, isPreview, entryUuid });
}

export function getPromoLotteries(promoUUID) {
    return fetches(`/promotions/${promoUUID}/lotteries`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

export function getLotteryInfo(promoUUID, lotteryUuid) {
    return fetches(`/promotions/${promoUUID}/lotteries/${lotteryUuid}`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

export function getLotteryPrizes(promoUUID, lotteryUuid) {
    return fetches(
        `/promotions/${promoUUID}/lotteries/${lotteryUuid}/prizes`,
        generateFetchOpts(),
        {
            dataKeys: 'data',
            throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
        }
    );
}

export function getUserLatestEntries(promoUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/latest?ts=${Date.now()}`,
        generateFetchOptsGetter(),
        {
            dataKeys: 'data',
            throwErrConfigs: { [clientErrTypes.GET_USER_LATEST_ENTRIES]: r => r.httpStatus !== 200 }
        }
    );
}

export function createUserEntries(promoUUID, turnstileToken, gpsPayload) {
    const testQs = getTestQueryString();
    const qs = testQs ? `?${querystring.stringify(testQs)}` : '';

    return fetches(
        `/promotions/${promoUUID}/entries${qs}`,
        generateFetchOptsGetter({
            method: POST,
            ...(Boolean(turnstileToken) ? { headers: { 'X-Cfts-Token': turnstileToken } } : {}),
            // GPS 座標 — Spec §3.4, coordinates: [longitude, latitude]（GeoJSON 格式）
            ...(gpsPayload ? { body: gpsPayload } : {})
        }),
        { dataKeys: 'data' }
    );
}

// token 可顯式帶入（OCEP 綁定頁 #86ey77cy4：tokenManager 以會員身分初始化，
// 好友狀態屬 LINE 身分需以 line token 呼叫）；未帶則沿用 tokenManager 當前身分
export function verifyLineFriend(promoUUID, { token } = {}) {
    return fetches(
        `/promotions/${promoUUID}/line/friendship/status`,
        token ? generateFetchOpts({ token }) : generateFetchOptsGetter(),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.LINE_FRIEND_QUALIFY_VALIDATION_FAILED]: r => r.httpStatus !== 200
            }
        }
    );
}

export function verifyKKboxMemberQualify(promoUUID) {
    return fetches(`/promotions/${promoUUID}/verify`, generateFetchOptsGetter(), {
        dataKeys: 'data'
    });
}

export function verityMemberQualification(promoUUID) {
    return fetches(`/promotions/${promoUUID}/qualification/verify`, generateFetchOptsGetter(), {
        dataKeys: 'data'
    });
}

// OCEP 會員 × LINE 綁定（#86ey77cy4）：檢查綁定狀態，唯讀、可重複呼叫。
// 單側登入也可打；token 可顯式帶入（如僅有 line token、tokenManager 尚未以該身分 init 時）
export function verifyMemberBindOcep(promoUUID, { token } = {}) {
    return fetches(
        `/promotions/${promoUUID}/account-link/check`,
        token ? generateFetchOpts({ token }) : generateFetchOptsGetter(),
        { dataKeys: 'data' }
    );
}

// OCEP 會員 × LINE 綁定（#86ey77cy4）：執行綁定（含覆蓋換綁）
export function bindMemberOcep(promoUUID, { enterpriseMemberUuid, memberLineId } = {}) {
    return fetches(
        `/promotions/${promoUUID}/account-link/confirm`,
        generateFetchOptsGetter({
            method: POST,
            body: {
                enterprise_member_uuid: enterpriseMemberUuid,
                member_line_id: memberLineId
            }
        }),
        { dataKeys: 'data' }
    );
}
// kkpoint
export function checkKKUserExchangePointsStatus(promoUUID, entryUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/point/status`,
        generateFetchOptsGetter()
    );
}

export function getKKboxUserPoints(promoUUID, entryUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/point`,
        generateFetchOptsGetter()
    );
}

export function exchangeKKboxPoints(promoUUID, entryUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/point/cost`,
        generateFetchOptsGetter({ method: POST })
    );
}
// General point
export function getUserPoints(promoUUID, entryUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/transactions/user/points`,
        generateFetchOptsGetter(),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.THIRD_PARTY_API_TIMED_OUT]: r =>
                    GET_USER_POINTS_API_RESPONSE.THIRD_PARTY_API_TIMED_OUT(r?.payload)
            }
        }
    );
}

export function exchangePoints(promoUUID, entryUUID) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/transactions/withdraw`,
        generateFetchOptsGetter({ method: POST })
    );
}

export function validateInvoiceApi({ promoUUID, entryUUID, body }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/validation/invoice`,
        generateFetchOptsGetter({ method: POST, body })
    );
}

export function validateTestCodeAPI({ host, sponsorId, testCode, testExpired } = {}) {
    return fetch(
        `${ZINES_API_ENDPOINT}/sponsors/${sponsorId}/verify-preview-code?host=${host}&preview-code=${testCode}&expired-at=${testExpired}`,
        {
            method: 'GET'
        }
    ).then(normalizeResponse);
}

export function uploadPhoto({
    promoUUID,
    entryUUID,
    file,
    compressType,
    seq,
    mediaUrl,
    mediaSource
}) {
    const formData = new FormData();

    // 傳遞 img URL 讓後端去抓該 URL img 再存到 S3 避免有些圖 CORS 前端無法轉為 blob 傳給後端
    if (Boolean(mediaUrl) && Boolean(mediaSource)) {
        formData.append('mediaUrl', mediaUrl);
        formData.append('mediaSource', mediaSource);
    } else {
        formData.append('image', file, file.name);
    }
    formData.append('compressType', compressType);
    formData.append('seq', seq);
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/media`,
        generateFetchOptsGetter({ method: POST, formData })
    );
}

/**
 * @description 上傳分享紀錄到後端（包含圖片 URL、尺寸等資訊），供後續分享紀錄查詢與分析使用
 * @see https://app.clickup.com/t/86ex144t2
 */
export function storeEntryShareRecord({ promoUUID, entryUUID, records }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/share-records`,
        generateFetchOptsGetter({ method: POST, body: records }),
        {
            throwErrConfigs: {
                [clientErrTypes.STORE_ENTRY_SHARE_RECORD_FAILED]: r =>
                    STORE_ENTRY_SHARE_RECORD_API_RESPONSE.RECORD_FAILED(r || {})
            }
        }
    );
}

export function submitPromo({ method = POST, promoUUID, entryUUID, headers, body }) {
    const testQs = getTestQueryString();
    const qs = testQs ? `?${querystring.stringify(testQs)}` : '';
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/result${qs}`,
        generateFetchOptsGetter({ method, headers, body }),
        {
            throwErrConfigs: {
                [clientErrTypes.STORE_GAME_RESULT_ENTRY_FLOW_EXISTS]: r =>
                    STORE_GAME_RESULT_API_RESPONSE.ENTRY_FLOW_EXISTS(r?.payload || {}) ||
                    STORE_GAME_RESULT_API_RESPONSE.ENTRY_FLOW_GAME_RESULT_EXISTS(r?.payload || {}),
                [clientErrTypes.STORE_GAME_RESULT_ENTRY_TOTAL_LIMIT]: r =>
                    STORE_GAME_RESULT_API_RESPONSE.ENTRY_TOTAL_LIMIT(r?.payload || {})
            }
        }
    );
}

export function getUserPasscodeStatus({ promoUUID, entryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/passcode/status`,
        generateFetchOpts()
    );
}

export function getPasscodeVerify({ promoUUID, passcode, turnstileToken }) {
    return fetches(
        `/promotions/${promoUUID}/passcode/${passcode}/verify`,
        generateFetchOpts({
            ...(Boolean(turnstileToken) ? { headers: { 'X-Cfts-Token': turnstileToken } } : {})
        })
    );
}

export function submitPasscode({ promoUUID, entryUUID, passcode }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/passcode`,
        generateFetchOpts({ method: POST, body: { passcode } }),
        {
            dataKeys: 'data'
        }
    );
}

export function getPromoClientOptionList({ promoUUID }) {
    return fetches(`/promotions/${promoUUID}/options`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

export function startGame({ promoUUID, entryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/game/start`,
        generateFetchOptsGetter({ method: POST }),
        {
            throwErrConfigs: {
                [clientErrTypes.MEMORY_MATCH_START_GAME_FAILED]: r => r.httpStatus !== 200
            }
        }
    );
}

export function getEntryFlow({ promoUUID, entryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/flows`,
        generateFetchOptsGetter()
    );
}

export function getPromoTaskListApi({ promoUUID }) {
    return fetches(`/promotions/${promoUUID}/tasks`, generateFetchOpts(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

// 後端追蹤 user 在 IG 聊天室點了哪些系統發送帶有 passcode 的活動網址
export function trackCampaignClick({ pcode }) {
    return fetches(
        `/promotions/campaigns/click?pcode=${pcode}`,
        generateFetchOpts({ method: POST }),
        {
            dataKeys: 'data',
            throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
        }
    );
}

/**
 * @method redeemPointsApi 取得活動使用的點數池清單
 * @param  {string} promoUUID 活動 Uuid
 * @param  {string} entryUUID entry Uuid
 * @param  {string} lotteryUUID 獎項 Uuid
 * @param  {string} prizeUUID 獎品 Uuid
 * @return  {promise}
 */
export function redeemPointsApi({ promoUUID, entryUUID, lotteryUUID, prizeUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/lotteries/${lotteryUUID}/prizes/${prizeUUID}/member/points/cost`,
        generateFetchOptsGetter({
            method: POST
        }),
        {
            dataKeys: 'data',
            throwErrConfigs: REDEEM_POINT_API_RESPONSE
        }
    );
}

/**
 * @method submitReferralCodeApi 送出邀請碼
 * @param  {string} promoUuid    活動 Uuid
 * @param  {string} entryUuid    Entry Uuid
 * @param  {string} referralCode 邀請碼
 * @return  {promise}
 */
export function submitReferralCodeApi({ promoUuid, entryUuid, referralCode }) {
    return fetches(
        `/promotions/${promoUuid}/entries/${entryUuid}/referral-code/${referralCode}`,
        generateFetchOptsGetter({ method: POST }),
        {
            dataKeys: 'data'
        }
    );
}
/**
 * @method checkReferralCodeApi 檢查邀請碼
 * @param  {string} promoUuid    活動 Uuid
 * @param  {string} referralCode 邀請碼
 * @return  {promise}
 */
export function checkReferralCodeApi({ promoUuid, referralCode }) {
    return fetches(`/promotions/${promoUuid}/referral-code/${referralCode}`, generateFetchOpts(), {
        dataKeys: 'data'
    });
}

/**
 * @method getPromoReferralInfoApi get 推薦邀請碼 & 短網址 & 推薦成功人數 api
 * @param  {string} promoUUID 活動 Uuid
 * @return  {promise}
 */
export function getPromoReferralInfoApi(promoUUID) {
    return fetches(`/promotions/${promoUUID}/referral-code`, generateFetchOptsGetter(), {
        dataKeys: 'data',
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

/**
 * @method getShareInfoApi 取得這檔活動的 OG 資料與 member name
 * @description https://app.clickup.com/t/860rv2g09
 * @param  {string} promoUUID 活動 Uuid
 * @param  {string=} entryUUID Entry Uuid，用來取對應的邀請人 member name 或是 share data
 * @param  {string=} referralCode 邀請碼，用來取對應的邀請人 member name
 * @return  {promise}
 */
export function getShareInfoApi(
    { promoUUID, entryUUID = undefined, referralCode = undefined },
    { signal } = {}
) {
    const args = {
        entryUuid: entryUUID,
        referralCode
    };
    const params = new URLSearchParams();
    // Set query parameters, automatically excluding undefined values
    for (const key in args) {
        if (args[key] !== undefined) {
            params.set(key, args[key]);
        }
    }
    const qs = params.toString();
    return fetches(
        `/promotions/${promoUUID}/share-info${qs ? `?${qs}` : ''}`,
        generateFetchOpts({ signal }),
        {
            dataKeys: 'data'
        }
    );
}

export function getShortLinkApi(promoUUID) {
    return fetches(`/promotions/${promoUUID}/short-link`, generateFetchOptsGetter(), {
        dataKeys: 'data'
    });
}

/**
 * @method getNotificationApi 取得通知 api
 * @description https://app.clickup.com/t/860ru7a42
 * @param  {string} promoUUID 活動 Uuid
 * @return  {promise}
 */
export function getNotificationApi(promoUUID) {
    return fetches(`/promotions/${promoUUID}/notifications`, generateFetchOptsGetter(), {
        dataKeys: 'data'
    });
}

/**
 * @method createNotificationApi 新增訂閱活動通知+取授權 api
 * @description https://app.clickup.com/t/860ru21af
 * @param  {string} promoUUID 活動 Uuid
 * @param  {string} sourceType 通知方式來源的類型 (1.後台 admin setting 的 email / 2.登入帳號的 email / 3.表單填寫的 email / 4.在前臺手動輸入的 email)
 * @param  {string} driver 通知的管道 (messageBirdEmail / lineNotify)
 * @param  {string} code line notify api 回傳的 code
 * @param  {string} email email address
 * @return  {promise}
 */
export function createNotificationApi({ promoUUID, sourceType, driver, code, email, redirectUrl }) {
    const subscriptionsPayload =
        driver === NOTIFY_DELIVER_TYPE_MAP.EMAIL ? { email } : { code, redirectUrl };
    const body = { sourceType, driver, ...subscriptionsPayload };
    return fetches(
        `/promotions/${promoUUID}/notifications`,
        generateFetchOptsGetter({ method: POST, body }),
        {
            dataKeys: 'data',
            throwErrConfigs: {
                [clientErrTypes.NOTIFY_CREATE_FAILED_BUT_NO_SENTRY]: r =>
                    POST_NOTIFY_API_RESPONSE.NOTIFY_CREATE_FAILED_BUT_NO_SENTRY(r?.payload || {}),
                [clientErrTypes.NOTIFY_CREATE_FAILED]: r =>
                    POST_NOTIFY_API_RESPONSE.NOTIFY_CREATE_FAILED(r?.httpStatus || {})
            }
        }
    );
}

/**
 * @method deleteNotificationApi 取消訂閱活動通知 api
 * @description https://app.clickup.com/t/860ru21zm
 * @param  {string} promoUUID 活動 Uuid
 * @return  {promise}
 */
export function deleteNotificationApi(promoUUID) {
    return fetches(
        `/promotions/${promoUUID}/notifications`,
        generateFetchOptsGetter({ method: DELETE }),
        {
            dataKeys: 'data'
        }
    );
}

/**
 * @method getChallengeRecordCard 取得闖關卡
 * @description https://app.clickup.com/t/86epzk6w8
 * @param  {string} promoUUID 活動 Uuid
 * @return  {promise}
 */
export function getChallengeRecordCard(promoUUID) {
    return fetches(`/promotions/${promoUUID}/check-point-card`, generateFetchOptsGetter(), {
        dataKeys: 'data'
    });
}

/**
 * @method checkTaskCompletedRecord 檢查任務完成紀錄
 * @description https://app.clickup.com/t/86epme2ky
 * @param  {string} promoUUID 活動 Uuid
 * @param  {string} taskUUID  關卡 Uuid
 * @return  {promise}
 */
export function checkTaskCompletedRecord(promoUUID, taskUUID) {
    return fetches(
        `/promotions/${promoUUID}/tasks/${taskUUID}`,
        generateFetchOptsGetter({ method: POST }),
        {
            dataKeys: 'data'
        }
    );
}

/**
 * @method getCumulativeQuizStatisticsApi 取得累積答題統計資料
 * @description https://app.clickup.com/t/86evdvv18
 * @param  {string} promotionUuid 活動 Uuid
 * @return  {promise}
 */
export function getCumulativeQuizStatisticsApi(promoUUID) {
    return fetches(`/promotions/${promoUUID}/member-entry-statistics`, generateFetchOptsGetter(), {
        dataKeys: 'data',
        passPayload: true,
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

/**
 * @method getOrderListApi 取得訂單列表 api
 * @description https://app.clickup.com/t/86epvyee1
 * @param  {string} promoUUID 活動 UUID
 * @param  {number} perPage 每頁幾筆
 * @param  {number} page 第幾頁
 * @return  {promise}
 */

export function getMemberOrderListApi({ promoUUID, perPage = 10, page }) {
    const qs = querystring.stringify({ perPage, page });

    return fetches(
        `/promotions/${promoUUID}/member/orders${qs ? `?${qs}` : ''}`,
        generateFetchOptsGetter({ method: GET })
    );
}

/**
 * @method getOrderListApi 特定 entry 的通過條件和 prize
 * @description https://app.clickup.com/t/86erz6v46
 * @param  {string} promoUUID 活動 UUID
 * @param  {string} entryUuid  entry UUID
 * @param  {number} [conditionType] task conditionType（機制 C 按受眾標籤給獎時需帶 15，見 https://app.clickup.com/t/86et0gjbh）
 * @return  {promise}
 */
export function getEntryPassTasksFilterListApi({ promoUUID, entryUuid, conditionType }) {
    const qs = conditionType != null ? `?${querystring.stringify({ conditionType })}` : '';

    return fetches(
        `/promotions/${promoUUID}/entries/${entryUuid}/prizes${qs}`,
        generateFetchOptsGetter({ method: GET })
    );
}

/**
 * @method getFormDataApi 取得表單資料 API
 * @description https://app.clickup.com/t/86eteaypg
 * @param  {string} formId 表單 ID
 * @param  {string} entryUuid 參加者 UUID
 * @param  {string} winnerUuid 得獎者 UUID
 * @return  {promise}
 */
export function getFormDataApi({ formId, entryUuid, winnerUuid }) {
    const params = { entryUuid, winnerUuid };
    const queryString = querystring.stringify(params, { skipNull: true });

    const url = `/forms/${formId}/member/answers${queryString ? `?${queryString}` : ''}`;
    return fetches(url, generateFetchOptsGetter({ method: GET }), {
        dataKeys: 'items',
        throwErrConfigs: GET_FORM_ANSWER_API_RESPONSE
    });
}

/**
 * @method getEntryRecordResultApi 取得參加者結果 API
 * @description https://app.clickup.com/t/86ett99z7
 * @param  {string} promoUUID 活動 UUID
 * @param  {string} entryUUID entry UUID
 * @return  {promise}
 */
export function getEntryRecordResultApi({ promoUUID, entryUUID }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/result`,
        generateFetchOptsGetter({ method: GET }),
        {
            dataKeys: 'data',
            passPayload: true
        }
    );
}

/**
 * 取得 AI Job 狀態
 * @description https://app.clickup.com/t/86ett14v7
 * @param {Object} params
 * @param {string} params.promoUUID - 活動 UUID
 * @param {string} params.entryUUID - Entry UUID
 * @returns {Promise<Array>} AI Job 狀態列表
 */
export const getAIJobsApi = ({ promoUUID, entryUUID }) => {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/ai-jobs`,
        generateFetchOptsGetter({ method: GET }),
        {
            dataKeys: 'data',
            passPayload: true,
            throwErrConfigs: {
                [clientErrTypes.GET_AI_JOBS_FAILED]: r => r.httpStatus !== 200
            }
        }
    );
};

/**
 * 更新 AI Job 狀態
 * @param {Object} params
 * @description https://app.clickup.com/t/86ett5qkk
 * @param {string} params.promoUUID - 活動 UUID
 * @param {string} params.entryUUID - Entry UUID
 * @param {string} params.jobUUID - Job UUID
 * @returns {Promise<Object>} 重新觸發生成式 Queue Job API
 */
export const retryAIJobApi = ({ promoUUID, entryUUID, jobUUID }) => {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUUID}/ai-jobs/${jobUUID}/re-trigger`,
        generateFetchOptsGetter({ method: POST }),
        {
            throwErrConfigs: {
                [clientErrTypes.JOB_IS_NOT_FOUND]: r =>
                    RETRY_AI_JOB_API_RESPONSE.JOB_IS_NOT_FOUND(r?.payload),
                [clientErrTypes.RETRY_AI_JOB_FAILED]: r =>
                    RETRY_AI_JOB_API_RESPONSE.RETRY_AI_JOB_FAILED(r)
            }
        }
    );
};

/**
 * @method submitImageResultApi store image result API
 * @description https://app.clickup.com/t/86ett1587
 * @param  {string} promotionUuid promotion UUID
 * @param  {string} entryUuid 參加者 UUID
 * @return  {promise}
 */
export function submitImageResult({ promoUUID, entryUuid, body }) {
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUuid}/image-upload`,
        generateFetchOptsGetter({ method: POST, body }),
        {
            dataKeys: 'data'
        }
    );
}

/**
 * 發票 OCR 辨識 API
 * @param {File} file - 上傳的圖片檔案
 * @param {string} promoUUID - 活動 UUID
 * @return {Promise} OCR 辨識結果
 */
export async function recognizeInvoiceImage({ file, promoUUID, entryUuid }) {
    const formData = new FormData();
    // 壓縮 / 補白後的圖片是 canvas.toBlob() 產出的 Blob，沒有 name，
    // 不補 filename 會送出 filename="blob"（無副檔名）。
    // 注意：這不是 403 的成因，已驗證帶 filename 的 File 同樣會被擋下。#86eyxwtty
    formData.append('image', file, file.name || 'invoice.jpg');
    return fetches(
        `/promotions/${promoUUID}/entries/${entryUuid}/ai/image-identify`,
        generateFetchOptsGetter({ method: POST, formData })
    );
}

/* ── 發票載具自動登錄（#86eytte5u、#86eyttg1t）────────────────────────────────
   POST 的 400 底下有五種 code、五種文案，所以**失敗要當回傳值**，不像多數 API 非 200 就丟。

   ⚠️ 四支 MUST NOT 補 `dataKeys: 'data'`：加了之後成功時 `payload` 就是 data 本身，
   呼叫端的 `const { code, data } = payload` 會拿到 undefined，store 被寫成 null
   ——**授權成功卻掉回未授權版面，且不報錯**。
   ──────────────────────────────────────────────────────────────────────── */

/** POST 專用：只對登入失效丟，其餘留成回傳值讓呼叫端依 `code` 分派 */
const throwOnUnauthorized = {
    [clientErrTypes.GET_API_ERROR]: r =>
        r.httpStatus === 401 || r?.payload?.code === receiptCarrierCodes.ACCESS_TOKEN_NOT_FOUND
};

/**
 * 查這位會員在本 Partner 的載具授權（活動層）
 *
 * 授權掛在 Partner 不是活動，路徑上的活動只用來認 Partner。
 * `data: null` ＝ 尚未授權，那是 200 不是錯誤。
 */
export function getReceiptCarrierAuthorization(promoUUID) {
    return fetches(`/promotions/${promoUUID}/receipt-carriers`, generateFetchOptsGetter(), {
        throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 }
    });
}

/**
 * 查這組授權涵蓋了哪些活動（sponsor 層，授權管理頁用）
 *
 * ⚠️ 要不要翻下一頁**只看 `hasNext`**：已結束的活動是後端查完才篩掉，某一頁可能不滿
 * `perPage`、甚至是空陣列，但後面還有。
 *
 * @param {string} loginType
 * @param {{page: number, perPage: number}} [pagination] 預設第 1 頁 10 筆，perPage 上限 100
 */
export function getReceiptCarrierPromotions(sponsorUUID, loginType, pagination = {}) {
    const qs = querystring.stringify(pagination);
    return fetches(
        `/promotions/sponsors/${sponsorUUID}/${loginType}/receipt-carriers${qs ? `?${qs}` : ''}`,
        generateFetchOptsGetter(),
        { throwErrConfigs: { [clientErrTypes.GET_API_ERROR]: r => r.httpStatus !== 200 } }
    );
}

/**
 * 延長授權（sponsor 層，授權管理頁用）
 *
 * ⚠️ **與 `bindOrRenewReceiptCarrier` 是兩支不同端點，不可互換**——那支是活動層的
 * （要 `promotionUuid`），管理頁不屬於任何活動。
 *
 * 不帶 body ＝ 單純續期（到期日重設為當日 + 180，**不呼叫財政部**）；
 * 帶 `cardEncrypt` ＝ 續期 ＋ 換驗證碼並把條碼改回有效（**會呼叫財政部**）。
 * 一律不帶 `cardNo`，由後端取授權列；帶了與授權列不同會回 400 `RCA-C0-FF1`。
 */
export function renewReceiptCarrierBySponsor(sponsorUUID, loginType, body) {
    return fetches(
        `/promotions/sponsors/${sponsorUUID}/${loginType}/receipt-carriers`,
        generateFetchOptsGetter({ method: POST, ...(body ? { body } : {}) }),
        { throwErrConfigs: throwOnUnauthorized }
    );
}

/**
 * 綁定或續期載具授權（活動層，活動頁用）
 *
 * 帶 `{ cardNo, cardEncrypt }` ＝ 綁定／重新綁定；**整個 body 省略 ＝ 續期**
 * （不呼叫財政部、也不動驗證碼）。只帶其中一個欄位會被判欄位驗證失敗。
 */
export function bindOrRenewReceiptCarrier(promoUUID, body) {
    return fetches(
        `/promotions/${promoUUID}/receipt-carriers`,
        generateFetchOptsGetter({ method: POST, ...(body ? { body } : {}) }),
        { throwErrConfigs: throwOnUnauthorized }
    );
}
